import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  Grid,
  Stack,
  Button,
  Box,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { toast } from 'react-toastify';
import CharCountField from '../../components/seo/CharCountField';
import SerpPreview from '../../components/seo/SerpPreview';
import SocialCardPreview from '../../components/seo/SocialCardPreview';
import { uploadViaBackend } from '../../utils/uploadImage';
import { getPageDetail, saveDraft, publishPage } from './seoApi';

const EMPTY = {
  title: '',
  description: '',
  ogTitle: '',
  ogDescription: '',
  ogImage: '',
  twitterCard: 'summary_large_image',
  canonicalUrl: '',
  noindex: false,
};

export default function SeoPageEditor() {
  const [params] = useSearchParams();
  const path = params.get('path') || '';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [entry, setEntry] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [status, setStatus] = useState('default');
  const [values, setValues] = useState(EMPTY);

  useEffect(() => {
    (async () => {
      const res = await getPageDetail(path);
      if (res?.status === 200) {
        setEntry(res.data.registryEntry);
        setDefaults(res.data.defaults || {});
        setStatus(res.data.status || 'default');
        setValues({ ...EMPTY, ...(res.data.draft || {}) });
      } else {
        toast.error(res?.data?.message || 'Failed to load page');
      }
      setLoading(false);
    })();
  }, [path]);

  const set = (name) => (e) =>
    setValues((v) => ({ ...v, [name]: e.target.value }));

  const handleOgUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        setUploading(true);
        const url = await uploadViaBackend(file);
        setValues((v) => ({ ...v, ogImage: url }));
        toast.success('Image uploaded');
      } catch (e) {
        toast.error('Image upload failed');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const doSave = async () => {
    setSaving(true);
    const res = await saveDraft(path, values);
    setSaving(false);
    if (res?.status === 200) {
      setStatus(res.data.status || 'draft');
      toast.success('Draft saved');
    } else if (res?.status === 400 && res.data?.errors) {
      res.data.errors.forEach((msg) => toast.error(msg));
    } else {
      toast.error(res?.data?.message || 'Failed to save draft');
    }
  };

  const doPublish = async () => {
    setPublishOpen(false);
    // Save the current draft first so publish reflects on-screen edits.
    const saveRes = await saveDraft(path, values);
    if (saveRes?.status !== 200) {
      if (saveRes?.status === 400 && saveRes.data?.errors) saveRes.data.errors.forEach((m) => toast.error(m));
      else toast.error('Could not save before publishing');
      return;
    }
    const res = await publishPage(path);
    if (res?.status === 200) {
      setStatus('published');
      toast.success('Published — live now');
    } else {
      toast.error(res?.data?.message || 'Failed to publish');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!entry) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">This page is not editable.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/dashboard/seo')}>Back to pages</Button>
      </Container>
    );
  }

  const previewTitle = values.title || defaults.title;
  const previewDesc = values.description || defaults.description;

  return (
    <Container maxWidth="lg">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4">{entry.label}</Typography>
          <Typography variant="body2" color="text.secondary">{entry.path}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {status === 'dirty' && <Chip size="small" color="warning" label="Unpublished changes" />}
          <Button variant="text" onClick={() => navigate(`/dashboard/seo/history?path=${encodeURIComponent(path)}`)}>
            History
          </Button>
          <Button variant="outlined" onClick={() => navigate('/dashboard/seo')}>Back</Button>
        </Stack>
      </Stack>

      {entry.compliance && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This is a legal/compliance page. Changes may require compliance review before publishing. Every version is retained.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Editor */}
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <CharCountField
                label="Page title"
                value={values.title}
                onChange={set('title')}
                recommended={60}
                max={120}
                placeholder={defaults.title}
                helperText="Shown as the clickable headline in Google results."
              />
              <CharCountField
                label="Meta description"
                value={values.description}
                onChange={set('description')}
                recommended={155}
                max={320}
                multiline
                rows={3}
                placeholder={defaults.description}
                helperText="The summary text under the link in Google results."
              />

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">SOCIAL SHARE (Facebook / LinkedIn)</Typography>
              </Divider>

              <CharCountField
                label="Social title (optional)"
                value={values.ogTitle}
                onChange={set('ogTitle')}
                recommended={70}
                max={120}
                placeholder={values.title || defaults.title}
                helperText="Falls back to the page title if left blank."
              />
              <CharCountField
                label="Social description (optional)"
                value={values.ogDescription}
                onChange={set('ogDescription')}
                recommended={120}
                max={320}
                multiline
                rows={2}
                placeholder={values.description || defaults.description}
                helperText="Falls back to the meta description if left blank."
              />

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Social share image</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="outlined" onClick={handleOgUpload} disabled={uploading}>
                    {uploading ? 'Uploading…' : 'Upload image'}
                  </Button>
                  {values.ogImage && (
                    <Button size="small" color="error" onClick={() => setValues((v) => ({ ...v, ogImage: '' }))}>
                      Remove
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">Recommended 1200×630 (PNG/JPG/WebP).</Typography>
              </Box>

              <FormControl size="small" fullWidth>
                <InputLabel>Twitter card style</InputLabel>
                <Select label="Twitter card style" value={values.twitterCard} onChange={set('twitterCard')}>
                  <MenuItem value="summary_large_image">Large image</MenuItem>
                  <MenuItem value="summary">Small (summary)</MenuItem>
                </Select>
              </FormControl>

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">ADVANCED</Typography>
              </Divider>

              <TextField
                size="small"
                label="Canonical URL (optional)"
                value={values.canonicalUrl}
                onChange={set('canonicalUrl')}
                placeholder={`https://jethl.com${path}`}
                helperText="Leave blank to use this page's own URL. Must be a path or point to jethl.com."
              />

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!values.noindex}
                      disabled={entry.locked}
                      onChange={(e) => setValues((v) => ({ ...v, noindex: e.target.checked }))}
                    />
                  }
                  label="Hide this page from Google (noindex)"
                />
                {entry.locked ? (
                  <Alert severity="info" sx={{ mt: 0.5 }}>
                    This page is required to stay indexed and cannot be hidden or redirected.
                  </Alert>
                ) : (
                  values.noindex && (
                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                      This will remove the page from Google search results once published.
                    </Alert>
                  )
                )}
              </Box>

              <Stack direction="row" spacing={2} pt={1}>
                <Button variant="outlined" onClick={doSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save draft'}
                </Button>
                <Button variant="contained" onClick={() => setPublishOpen(true)} disabled={saving}>
                  Publish
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Grid>

        {/* Live previews */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Google result preview</Typography>
              <SerpPreview path={path} title={previewTitle} description={previewDesc} />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Social share preview</Typography>
              <SocialCardPreview
                path={path}
                title={previewTitle}
                description={previewDesc}
                ogTitle={values.ogTitle}
                ogDescription={values.ogDescription}
                ogImage={values.ogImage}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)}>
        <DialogTitle>Publish changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will update what visitors and search engines see for <strong>{entry.path}</strong> immediately.
            {entry.compliance && ' This is a legal/compliance page — confirm the wording has been reviewed.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doPublish}>Publish now</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
